import {
    type FC,
    type ReactNode,
    createContext,
    useContext,
    useMemo,
    useState,
    useRef,
    useEffect,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { trackSoldPdpSponsoredYmalGridTest } from '../../utils/abTestHelper.ts';
import { type PdpSimilarItemsSponsoredMetadata } from './mergePdpSimilarItemsAndSponsoredItems.ts';
import { isEligibleForSoldPdpSponsoredYmalGrid } from './isEligibleForSoldPdpSponsoredYmalGrid.ts';
import {
    type GridSponsoredItem,
    usePdpSharedItemsSponsoredLoadMore,
} from './usePdpSharedSimilarItemsSponsoredLoadMore.ts';
import {
    SOLD_PDP_YMAL_MAX_GRID_SPONSORED,
    SOLD_PDP_YMAL_SL_INITIAL,
    SOLD_PDP_YMAL_ITEMS_PER_ROW,
    SOLD_PDP_YMAL_SL_SUBSEQUENT,
} from '../PdpRespSimilarItems/pdpRespSimilarItemsConstants.ts';
import {
    SOLD_MW_PDP_YMAL_ITEMS_PER_ROW,
    SOLD_MW_PDP_YMAL_SL_INITIAL,
    SOLD_MW_PDP_YMAL_SL_SUBSEQUENT,
} from '../PdpMobileRelatedItems/pdpMobileRelatedItemsConstants.ts';

import { type PdpSharedSimilarSponsoredItemsProvider_viewer$key } from './__generated__/PdpSharedSimilarSponsoredItemsProvider_viewer.graphql.ts';

type PdpSponsoredCarouselCounts = {
    eligibleItemIdsCount: number;
    inflatedItemsCount: number;
};

type PdpSharedSimilarSponsoredItemsContextType = {
    /** AB variant; false until afterDisplay viewer is passed. */
    isSoldPdpSponsoredYmalGridVariant: boolean;
    /** AB variant + ≥1 full grid SL row (desktop 5-up or MW 2-up). */
    isEffectiveVariant: boolean;
    /** Called by the featured carousel once it knows its SL pool sizes (both AB arms). */
    registerCarouselSponsoredCounts: (counts: PdpSponsoredCarouselCounts) => void;
    gridSponsoredItems: readonly GridSponsoredItem[];
    /** Inflated SLs past the grid cap — empty means hide featured carousel. */
    featuredCarouselSponsoredItems: readonly GridSponsoredItem[];
    metadataByItemId: Record<string, PdpSimilarItemsSponsoredMetadata>;
    sponsoredPagesLoaded: number;
    hasNextSponsored: boolean;
    isLoadingNextSponsored: boolean;
    hasSponsoredLoadError: boolean;
    inflateNextSponsored: (count?: number) => Promise<void>;
    isSponsoredListingsLoaded: boolean;
    gridSponsoredListingsIdsCount: number;
};

const emptyContext: PdpSharedSimilarSponsoredItemsContextType = {
    isSoldPdpSponsoredYmalGridVariant: false,
    isEffectiveVariant: false,
    registerCarouselSponsoredCounts: () => {},
    gridSponsoredItems: [],
    featuredCarouselSponsoredItems: [],
    metadataByItemId: {},
    sponsoredPagesLoaded: 0,
    hasNextSponsored: false,
    isLoadingNextSponsored: false,
    hasSponsoredLoadError: false,
    inflateNextSponsored: () => Promise.resolve(),
    isSponsoredListingsLoaded: false,
    gridSponsoredListingsIdsCount: 0,
};

const PdpSharedSimilarSponsoredItemsContext =
    createContext<PdpSharedSimilarSponsoredItemsContextType>(emptyContext);

export const usePdpSharedSimilarSponsoredItemsContext =
    (): PdpSharedSimilarSponsoredItemsContextType =>
        useContext(PdpSharedSimilarSponsoredItemsContext);

const viewerFragment = graphql`
    fragment PdpSharedSimilarSponsoredItemsProvider_viewer on Viewer {
        sponsoredItems(
            userId: $userId
            guestId: $guestId
            itemId: $itemId
            pageSize: $numOfSponsoredItems
            maxItems: $maxSponsoredItems
            minItemsSteps: $minSponsoredItemsSteps
        ) {
            eligibleItemIds
            metadata {
                itemId
                impressionTrackerLink
                clickTrackerLink
            }
            items {
                serviceId
                title
                browseUrl
                categoryCode
                ecommerceTrackingParams
                ...PdpRespSimilarItemsGrid_similarItems
                ...SharedRespRelatedItem_item @arguments(priceBookName: $priceBookName)
                ...SharedMobileRelatedItem_item @arguments(priceBookName: $priceBookName)
                ...FavoritesProvider_item
            }
        }
    }
`;

export const PdpSharedSimilarSponsoredItemsProvider: FC<{
    viewer: PdpSharedSimilarSponsoredItemsProvider_viewer$key | null | undefined;
    isTrade: boolean;
    priceBookName?: string;
    children: ReactNode;
}> = ({ viewer: viewerRef, isTrade, priceBookName = '', children }) => {
    const { isMobile, soldPdpSponsoredYmalGridTest } = useServerVarsContext();
    const isSoldPdpSponsoredYmalGridVariant = soldPdpSponsoredYmalGridTest === 'variant';
    const itemsPerRow =
        isMobile && isSoldPdpSponsoredYmalGridVariant
            ? SOLD_MW_PDP_YMAL_ITEMS_PER_ROW
            : SOLD_PDP_YMAL_ITEMS_PER_ROW;
    const initialSponsoredListings =
        isMobile && isSoldPdpSponsoredYmalGridVariant
            ? SOLD_MW_PDP_YMAL_SL_INITIAL
            : SOLD_PDP_YMAL_SL_INITIAL;
    const subsequentSponsoredListings =
        isMobile && isSoldPdpSponsoredYmalGridVariant
            ? SOLD_MW_PDP_YMAL_SL_SUBSEQUENT
            : SOLD_PDP_YMAL_SL_SUBSEQUENT;
    const viewer = useFragment(
        viewerFragment,
        isSoldPdpSponsoredYmalGridVariant ? viewerRef : null
    );

    const sponsoredItems = viewer?.sponsoredItems?.items;
    const sponsoredMetadata = viewer?.sponsoredItems?.metadata;

    const eligibleItemIds = useMemo(
        () => (viewer?.sponsoredItems?.eligibleItemIds || []).filter(filterFalsy),
        [viewer?.sponsoredItems?.eligibleItemIds]
    );

    // Control never fetches the provider fragment, so the featured carousel (fetched in both
    // arms) reports its SL pool sizes; the provider owns the eligibility rule so it lives in
    // one place.
    const hasTrackedRef = useRef(false);
    const [carouselCounts, setCarouselCounts] = useState<PdpSponsoredCarouselCounts | null>(null);
    const isEligibleForAbTracking =
        !!carouselCounts &&
        carouselCounts.inflatedItemsCount > 0 &&
        isEligibleForSoldPdpSponsoredYmalGrid({ ...carouselCounts, itemsPerRow });

    useEffect(() => {
        if (hasTrackedRef.current || !isEligibleForAbTracking) {
            return;
        }
        hasTrackedRef.current = true;
        trackSoldPdpSponsoredYmalGridTest();
    }, [isEligibleForAbTracking]);

    const isEffectiveVariant =
        isSoldPdpSponsoredYmalGridVariant &&
        isEligibleForSoldPdpSponsoredYmalGrid({
            eligibleItemIdsCount: eligibleItemIds.length,
            inflatedItemsCount: (sponsoredItems || []).filter(filterFalsy).length,
            itemsPerRow,
        });

    const {
        extraGridItems,
        carouselLeftoverItems,
        sponsoredPagesLoaded,
        hasNextSponsored: hasNextSponsoredFromPool,
        isLoadingNextSponsored,
        hasSponsoredLoadError,
        inflateNextSponsored,
    } = usePdpSharedItemsSponsoredLoadMore({
        enabled: isEffectiveVariant,
        eligibleItemIds,
        isTrade,
        priceBookName,
        // Initial query inflates initialSponsoredListings; remaining pool IDs load with YMAL pages.
        initialOffset: initialSponsoredListings,
        itemsPerRow,
        subsequentCount: subsequentSponsoredListings,
    });

    const value = useMemo((): PdpSharedSimilarSponsoredItemsContextType => {
        const initialItems = (sponsoredItems || []).filter(filterFalsy);
        const allInflatedSponsoredItems = isEffectiveVariant
            ? ([...initialItems, ...extraGridItems] as GridSponsoredItem[])
            : [];
        const gridSponsoredItems = allInflatedSponsoredItems.slice(
            0,
            SOLD_PDP_YMAL_MAX_GRID_SPONSORED
        );
        const featuredCarouselSponsoredItems = isEffectiveVariant ? carouselLeftoverItems : [];
        const metadataByItemId = !isEffectiveVariant
            ? {}
            : (sponsoredMetadata || [])
                  .filter(filterFalsy)
                  .reduce<Record<string, PdpSimilarItemsSponsoredMetadata>>((acc, meta) => {
                      if (meta.itemId) {
                          acc[meta.itemId] = {
                              impressionTrackerLink: meta.impressionTrackerLink,
                              clickTrackerLink: meta.clickTrackerLink,
                          };
                      }
                      return acc;
                  }, {});
        const shouldAwaitSponsoredListings = isSoldPdpSponsoredYmalGridVariant;
        const cappedCount = eligibleItemIds.slice(0, SOLD_PDP_YMAL_MAX_GRID_SPONSORED).length;
        const gridSponsoredListingsIdsCount = cappedCount - (cappedCount % itemsPerRow);

        return {
            isSoldPdpSponsoredYmalGridVariant,
            isEffectiveVariant,
            registerCarouselSponsoredCounts: setCarouselCounts,
            gridSponsoredItems,
            featuredCarouselSponsoredItems,
            metadataByItemId,
            sponsoredPagesLoaded,
            hasNextSponsored:
                hasNextSponsoredFromPool &&
                gridSponsoredItems.length < SOLD_PDP_YMAL_MAX_GRID_SPONSORED,
            isLoadingNextSponsored,
            hasSponsoredLoadError,
            inflateNextSponsored,
            isSponsoredListingsLoaded: !shouldAwaitSponsoredListings || viewer !== null,
            gridSponsoredListingsIdsCount,
        };
    }, [
        extraGridItems,
        carouselLeftoverItems,
        hasNextSponsoredFromPool,
        hasSponsoredLoadError,
        isEffectiveVariant,
        isLoadingNextSponsored,
        isSoldPdpSponsoredYmalGridVariant,
        inflateNextSponsored,
        sponsoredItems,
        sponsoredMetadata,
        sponsoredPagesLoaded,
        eligibleItemIds,
        itemsPerRow,
        viewer,
    ]);

    return (
        <PdpSharedSimilarSponsoredItemsContext value={value}>
            {children}
        </PdpSharedSimilarSponsoredItemsContext>
    );
};
